import { useCallback, useEffect, useRef, useState } from 'react';
import { showToast } from '@attentive-platform/stem-ui';

import { fetchScopeRecords, mapRecordListRow } from './api';

export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

const STATE_MAP = {
  'All states': undefined,
  New: 'new',
  Sent: 'sent',
  Submitted: 'submitted',
  Reopened: 'reopened',
  'Sent · expired': 'sent_link_expired',
  'Reopened · expired': 'reopened_link_expired'
};
const PILOT_STATUS_MAP = {
  'Pilot: open': 'open',
  'Pilot: closed won': 'closed_won',
  'Pilot: closed lost': 'closed_lost'
};
const WINDOW_MAP = {
  'Last 30 days': '30',
  'Last 90 days': '90',
  'This quarter': '90',
  'All time': 'all'
};

const buildParams = ({ linked, query, stateFilter, userFilter, pilotFilter, dateFilter }) => ({
  linked,
  q: query?.trim() || undefined,
  scope_form_status: STATE_MAP[stateFilter],
  internal_user: userFilter && userFilter !== 'All internal users' ? userFilter : undefined,
  pilot_status: PILOT_STATUS_MAP[pilotFilter],
  window: WINDOW_MAP[dateFilter]
});

const useScopeRecords = ({ linked, query, stateFilter, userFilter, pilotFilter, dateFilter }) => {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [refetchToken, setRefetchToken] = useState(0);
  const abortRef = useRef(null);

  useEffect(() => {
    setPage(1);
  }, [linked, query, stateFilter, userFilter, pilotFilter, dateFilter, pageSize]);

  useEffect(() => {
    const id = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      const { data, error: err } = await fetchScopeRecords({
        ...buildParams({ linked, query, stateFilter, userFilter, pilotFilter, dateFilter }),
        page,
        limit: pageSize,
        signal: controller.signal
      });
      if (controller.signal.aborted) return;
      if (err) {
        const msg = typeof err === 'string' ? err : err.detail || 'Failed to load records';
        showToast(msg, 'error');
        setRows([]);
        setCount(0);
      } else {
        const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
        setRows(results.map(mapRecordListRow));
        setCount(data?.count ?? results.length);
      }
      setLoading(false);
    }, 250);
    return () => clearTimeout(id);
  }, [linked, query, stateFilter, userFilter, pilotFilter, dateFilter, page, pageSize, refetchToken]);

  const refetch = useCallback(() => setRefetchToken(t => t + 1), []);
  const pageCount = count ? Math.max(1, Math.ceil(count / pageSize)) : 0;

  return { rows, count, loading, refetch, page, setPage, pageSize, setPageSize, pageCount };
};

export default useScopeRecords;
